<template>
  <div class="result-container">
    <div class="bg-secondary-3 rounded-top p-2">
      <div class="row mx-0 py-2 bg-white rounded align-items-center">
        <div class="col-12 col-md-3 d-flex align-items-center">
          <span class="fa-stack color-orange">
            <em class="fas fa-circle fa-stack-2x" />
            <em class="fas fa-file-invoice fa-stack-1x fa-inverse" />
          </span>
          <span class="font-weight-bold ml-3">
            {{ item.businessId }}
          </span>
        </div>
        <div class="col-12 col-md-3">
          <span class="data-title">
            <em class="fas fa-ballot-check color-orange mr-1" />
            Estudiante:
          </span>
          <span class="label">
            {{ item.student }}
          </span>
        </div>
        <div class="col-12 col-md-3">
          <span class="data-title">
            <em class="fas fa-ballot-check color-orange mr-1" />
            Alternativa de Ubicaci&oacute;n:
          </span>
          <span class="label">
            {{ item.alternativeUbication }}
          </span>
        </div>
        <div class="col-12 col-md-auto">
          <span class="data-title">
            <em class="fas fa-ballot-check color-orange mr-1" />
            Grado:
          </span>
          <span class="label">
            {{ item.grade }}
          </span>
        </div>
        <div class="col-12 col-md-auto ml-auto">
          <span class="data-title">
            <em class="fas fa-ballot-check color-orange mr-1" />
            Cantidad de Estudiantes:
          </span>
          <span class="label">
            {{ item.studentsQuantity }}
          </span>
        </div>
      </div>
    </div>
    <custom-callout
      class-main="rounded"
      :controlled="true"
      :opened="item.isOpen"
      @click:open="$emit('on-open-close', item.isOpen)"
    >
      <div class="row py-2 mx-0">
        <div
          class="col-12 col-md-2 mb-3 mb-md-0 d-flex justify-content-between align-items-center d-md-block border-right"
        >
          <div class="mt-2 mt-md-0 w-100">
            <div
              class="px-4 py-1 rounded-pill text-center font-14 font-weight-bold fit-md-content mt-1 mb-2"
              :class="`bg-${getStatusColor}`"
            >
              {{ item.statusName }}
            </div>
            <div class="label ml-0">
              <span class="color-gray-6 mr-1">No.</span>
              <b>{{ item.validityId }}</b>
            </div>
          </div>
        </div>
        <div class="col row mt-2 mt-md-0 ml-md-3">
          <div class="col-12 col-md-4 mt-md-2 mb-3 mb-md-0">
            <span class="data-title">Maestro:</span>
            {{ item.teacherName }}
          </div>
          <div class="col-12 col-md-4 mb-3 mb-md-0">
            <validation-provider
              v-slot="{ errors }"
              :rules="`required|max_date:${item.assignmentEndDate}`"
              :custom-messages="{
                required: 'Favor de entrar fecha desde.',
              }"
            >
              <custom-date-picker
                v-model="item.assignmentStartDate"
                label="Fecha Desde:"
                :min-date="approvalDate"
                placeholder="DD/MM/YYYY"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
              />
            </validation-provider>
          </div>
          <div class="col-12 col-md-4 mb-3 mb-md-0">
            <validation-provider
              v-slot="{ errors }"
              :rules="`required|min_date:${item.assignmentStartDate}`"
              :custom-messages="{
                required: 'Favor de entrar fecha hasta.',
              }"
            >
              <custom-date-picker
                v-model="item.assignmentEndDate"
                label="Fecha Hasta:"
                placeholder="DD/MM/YYYY"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
              />
            </validation-provider>
          </div>
        </div>
      </div>
      <template #edition>
        <div class="bg-secondary-3 p-2">
          <div class="bg-white rounded p-3">
            <custom-tab>
              <custom-tab-item lazy title="Información General">
                <general-info
                  class="mt-2 mx-3"
                  :request-id="item.requestId"
                  :academic-year-id="academicYearId"
                />
              </custom-tab-item>
              <custom-tab-item lazy title="Servicios">
                <validity-services
                  class="mt-2 mx-3"
                  :request-id="item.requestId"
                  :academic-year-id="academicYearId"
                />
              </custom-tab-item>
            </custom-tab>
          </div>
        </div>
      </template>
    </custom-callout>
  </div>
</template>

<script>
import CustomCallout from 'custom-callout';
import CustomDatePicker from 'custom-date-picker';
import { tapRequestStatusesEnum } from '@/components/pei/ubication-tech-assistance/_types.js';
import { extend } from 'vee-validate';
import moment from 'moment';
import CustomTab from 'custom-tabs/src/CustomTab';
import CustomTabItem from 'custom-tabs/src/CustomTabItem';
import GeneralInfo from '@/components/pei/ubication-tech-assistance/validityRequest/components/GeneralInfo.vue';
import ValidityServices from '@/components/pei/ubication-tech-assistance/validityRequest/components/ValidityServices.vue';

extend('min_date', {
  validate: (value, { minDate }) => {
    return moment(value).isSameOrAfter(moment(minDate));
  },
  params: ['minDate'],
  message: 'Fecha de vigencia hasta no puede ser menor a la desde.',
});

extend('max_date', {
  validate: (value, { maxDate }) => {
    return maxDate != 'null'
      ? moment(value).isSameOrBefore(moment(maxDate))
      : true;
  },
  params: ['maxDate'],
  message: 'Fecha de vigencia desde no puede ser mayor a la hasta.',
});

export default {
  name: 'SearchResult',
  components: {
    CustomCallout,
    CustomDatePicker,
    CustomTab,
    CustomTabItem,
    GeneralInfo,
    ValidityServices,
  },
  props: {
    item: {
      type: Object,
      default: () => [],
    },
    academicYearId: {
      type: Number,
      default: null,
    },
  },
  computed: {
    approvalDate() {
      const approvalDate = moment(this.item.approvalDate).subtract(1, 'days');
      return approvalDate.format('MM-DD-YYYY');
    },
    getStatusColor() {
      switch (this.item.statusId) {
        case tapRequestStatusesEnum.Asignado:
          return 'yellow';
        case tapRequestStatusesEnum['Proceso de Enmienda']:
        case tapRequestStatusesEnum.Enmendado:
          return 'gray-2';
        case tapRequestStatusesEnum.NoVigenteBaja:
        case tapRequestStatusesEnum.NoVigenteTraslado:
          return 'primary-9';
        default:
          return 'yellow';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.result-container ::v-deep {
  .data-label {
    font-size: 14px !important;
  }
  .data-title {
    display: block;
  }
  .label {
    font-size: 0.875rem;
    margin-left: 1rem;
  }
  @media only screen and (min-width: 768px) {
    .fit-md-content {
      width: fit-content;
    }
  }
}
</style>
